import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink, faPlus } from '@fortawesome/free-solid-svg-icons';
import { faInstagram, faFacebook, faTwitter, faLinkedin } from '@fortawesome/free-brands-svg-icons';
import { useAuth } from '../context/AuthContext';
import { creditsAPI, analysisJobsAPI } from '../services/api';

const SocialIntegration: React.FC = () => {
  const { business } = useAuth();
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimatedCredits, setEstimatedCredits] = useState<number | null>(null);
  const [currentAnalysis, setCurrentAnalysis] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  return (
    <section id="social-integration" className="mb-8">
      <div className="backdrop-blur-xl bg-white/5 p-6 rounded-2xl border border-white/10">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center">
              <FontAwesomeIcon icon={faLink} className="text-white" />
            </div>
            <h2 className="text-xl font-semibold text-white">Sosyal Medya Hesapları</h2>
          </div>
          <button className="bg-gradient-to-r from-blue-400 to-blue-600 px-4 py-2 rounded-xl text-white font-medium hover:opacity-90 transition-opacity">
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Hesap Ekle
          </button>
        </div>
        {business && (
          <div className="mb-4 flex flex-wrap gap-2">
            {[
              { key: 'instagram', label: 'Instagram' },
              { key: 'facebook', label: 'Facebook' },
              { key: 'twitter', label: 'X' },
              { key: 'linkedin', label: 'LinkedIn' },
            ].map(p => {
              const platform: any = (business as any)[p.key];
              if (!platform) return null;
              const status = platform.syncStatus as string | undefined;
              const cls =
                status === 'completed'
                  ? 'bg-green-500/10 text-green-300 border border-green-500/20'
                  : status === 'pending'
                    ? 'bg-yellow-500/10 text-yellow-300 border border-yellow-500/20'
                    : status === 'failed'
                      ? 'bg-red-500/10 text-red-300 border border-red-500/20'
                      : 'bg-white/5 text-white/70 border border-white/10';
              return (
                <div key={p.key} className={`inline-flex items-center px-3 py-1.5 rounded-xl text-xs ${cls}`}>
                  <span className="mr-2 opacity-80">{p.label}</span>
                  <span className="opacity-100">
                    {status === 'completed' && 'tamamlandı'}
                    {status === 'pending' && 'bekliyor/çalışıyor'}
                    {status === 'failed' && 'hata'}
                    {!status && 'bilinmiyor'}
                  </span>
                  {platform.syncAt && (
                    <span className="ml-2 opacity-60">
                      • {new Date(platform.syncAt).toLocaleString()}
                    </span>
                  )}
                  <button
                    disabled={status !== 'completed'}
                    className="ml-3 px-2.5 py-1 rounded-lg bg-white/10 border border-white/20 text-white hover:bg-white/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={async () => {
                      setCurrentAnalysis(p.key);
                      setAiModalOpen(true);
                      setEstimatedCredits(null);
                      // Set default dates (last 30 days)
                      const today = new Date();
                      const thirtyDaysAgo = new Date();
                      thirtyDaysAgo.setDate(today.getDate() - 30);
                      setEndDate(today.toISOString().split('T')[0]);
                      setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
                    }}
                  >
                    AI Analize Başla
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {aiModalOpen && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setAiModalOpen(false)}></div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gray-900 rounded-2xl border border-white/10 w-full max-w-md overflow-hidden">
              <div className="p-6 border-b border-white/10">
                <h3 className="text-xl font-bold text-white">AI Analizi {currentAnalysis ? `(${currentAnalysis})` : ''}</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">Başlangıç Tarihi</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      max={endDate || new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">Bitiş Tarihi</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      min={startDate}
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                    />
                  </div>
                </div>
                <div className="text-white/80">
                  Tahmini harcanacak kredi:
                  <div className="mt-2 text-2xl font-semibold text-white">
                    {estimateLoading ? 'Hesaplanıyor...' : (estimatedCredits !== null 
                      ? Number(estimatedCredits).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                      : '—')}
                  </div>
                </div>
                <button
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-700 py-2 px-4 rounded-xl text-white font-medium hover:from-blue-600 hover:to-blue-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!startDate || !endDate}
                  onClick={async () => {
                    if (!currentAnalysis || !startDate || !endDate) return;
                    setEstimateLoading(true);
                    setEstimatedCredits(null);
                    try {
                      const res = await creditsAPI.estimate(currentAnalysis, startDate, endDate);
                      const value = res?.data?.estimatedCredits ?? res?.data?.data?.estimatedCredits ?? null;
                      setEstimatedCredits(typeof value === 'number' ? value : null);
                    } finally {
                      setEstimateLoading(false);
                    }
                  }}
                >
                  Kredi Hesapla
                </button>
                <button
                  className="w-full bg-gradient-to-r from-purple-500 to-purple-700 py-3 px-6 rounded-xl text-white font-semibold hover:from-purple-600 hover:to-purple-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={estimateLoading || estimatedCredits === null || !startDate || !endDate}
                  onClick={async () => {
                    try {
                      if (!currentAnalysis || !startDate || !endDate) return;
                      setEstimateLoading(true);
                      await analysisJobsAPI.start(currentAnalysis, startDate, endDate);
                    } finally {
                      setEstimateLoading(false);
                      setAiModalOpen(false);
                    }
                  }}
                >
                  Harca
                </button>
                <button
                  className="w-full mt-2 bg-white/10 border border-white/20 py-3 px-6 rounded-xl text-white hover:bg-white/20 transition"
                  onClick={() => {
                    setAiModalOpen(false);
                    setStartDate('');
                    setEndDate('');
                    setEstimatedCredits(null);
                  }}
                >
                  İptal
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="backdrop-blur-xl bg-white/5 p-4 rounded-xl border border-green-400/20">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <FontAwesomeIcon icon={faInstagram} className="text-2xl text-pink-400" />
                <div>
                  <h4 className="text-white font-medium">Instagram</h4>
                  <p className="text-white/50 text-sm">@winnomi_official</p>
                </div>
              </div>
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-white/70">Takipçi</span>
                <span className="text-white">124.5K</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/70">Etkileşim</span>
                <span className="text-green-400">4.2%</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/70">Son Paylaşım</span>
                <span className="text-white/70">2s önce</span>
              </div>
            </div>
          </div>

          <div className="backdrop-blur-xl bg-white/5 p-4 rounded-xl border border-green-400/20">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <FontAwesomeIcon icon={faFacebook} className="text-2xl text-blue-400" />
                <div>
                  <h4 className="text-white font-medium">Facebook</h4>
                  <p className="text-white/50 text-sm">Winnomi Analytics</p>
                </div>
              </div>
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-white/70">Takipçi</span>
                <span className="text-white">89.2K</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/70">Etkileşim</span>
                <span className="text-green-400">3.8%</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/70">Son Paylaşım</span>
                <span className="text-white/70">5s önce</span>
              </div>
            </div>
          </div>

          <div className="backdrop-blur-xl bg-white/5 p-4 rounded-xl border border-yellow-400/20">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <FontAwesomeIcon icon={faTwitter} className="text-2xl text-blue-300" />
                <div>
                  <h4 className="text-white font-medium">Twitter</h4>
                  <p className="text-white/50 text-sm">@winnomi_ai</p>
                </div>
              </div>
              <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-white/70">Takipçi</span>
                <span className="text-white">45.8K</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/70">Etkileşim</span>
                <span className="text-yellow-400">2.9%</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/70">Son Paylaşım</span>
                <span className="text-white/70">1s önce</span>
              </div>
            </div>
          </div>

          <div className="backdrop-blur-xl bg-white/5 p-4 rounded-xl border border-white/10 opacity-50">
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <FontAwesomeIcon icon={faLinkedin} className="text-3xl text-white/30 mb-2" />
                <p className="text-white/50 text-sm">LinkedIn Bağla</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialIntegration;
